import React, { useState, useRef, useEffect } from 'react';
import { XCircle, Plus, Trash } from "lucide-react";

import { useStore } from '@/store/store';
import 'mathlive';
// Import the type definition for the element instance
import type { MathfieldElement } from 'mathlive';
import 'katex/dist/katex.min.css';
import { Button } from '../ui/button';

export interface MathTextEditorProps {
    onFormulaInserted?: (latex: string, fullText: string) => void;
}

// --- TYPE AUGMENTATION TO FIX TS ERROR ---

// 1. **Augment the MathfieldElement Type**
//    This tells TypeScript that the MathfieldElement *does* have these methods/properties 
//    which may be missing from the bundled type definitions in some environments.
declare module 'mathlive' {
    interface MathfieldElement {
        // Properties and methods used programmatically in the component:
        virtualKeyboardMode: 'auto' | 'manual' | 'onfocus';
        showVirtualKeyboard(): void;
        hideVirtualKeyboard(): void;
        focus(): void;
        value: string;
    }
}

// 2. **Augment the JSX Intrinsic Elements**
//    This fixes the error related to the JSX tag name <math-field>.
declare global {
    namespace JSX {
        interface IntrinsicElements {
            'math-field': React.DetailedHTMLProps<
                React.HTMLAttributes<MathfieldElement>,
                MathfieldElement
            > & {
                // Define any custom properties used as attributes
                'virtual-keyboard-mode'?: 'auto' | 'manual' | 'onfocus';
            };
            'math-span': React.DetailedHTMLProps<
                React.HTMLAttributes<HTMLElement>,
                HTMLElement
            >
        }
    }
}

// ------------------------------------------

export default function MathTextEditor({ onFormulaInserted }: MathTextEditorProps) {
    const [setLatexPreview] = useState<string>('');
    const [isToolbarOpen, setIsToolbarOpen] = useState<boolean>(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const mathFieldRef = useRef<MathfieldElement | null>(null);
    const { setMathEditor, mathEditor } = useStore();
    useEffect(() => {
        // Access the Mathfield element via the ref
        const mathfield = mathFieldRef.current;

        if (mathfield) {
            showVirtualKeyboard()
        }

        // Cleanup: Hide the keyboard when the component unmounts
        return () => {
            if (mathfield) {
                // mathfield.hideVirtualKeyboard();
            }
        };
    }, [mathEditor]);
    const showVirtualKeyboard = () => {
        try {
            mathFieldRef.current?.executeCommand('showVirtualKeyboard');
        } catch (e) {
            console.log("Eroare", e)
        }
    };


    const closeToolbar = () => {
        setIsToolbarOpen(false);
        textAreaRef.current?.focus();
        setMathEditor(false)
    };

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            closeToolbar();
        }
    };

    const handleMathFieldInput = () => {
        const latex = mathFieldRef.current?.getValue('latex') || '';
        setLatexPreview(latex);
    };

    const handleMathFieldPointerDown = () => {
        setTimeout(() => {
            mathFieldRef.current?.focus();
            showVirtualKeyboard();
        }, 0);
    };

    const insertFormula = () => {
        const latex = mathFieldRef.current?.getValue('latex') || '';
        if (!latex.trim()) {
            alert('Nu există nicio formulă de inserat.');
            return;
        }

        const toInsert = ` $$${latex}$$ `;
        if (onFormulaInserted) {
            // Trimite LaTeX-ul pur și textul formatat înapoi
            onFormulaInserted(toInsert);
        }
        closeToolbar();

    };

    const clearMath = () => {
        if (mathFieldRef.current) {
            mathFieldRef.current.setValue('', { silenceNotifications: true });
            setLatexPreview('');
            mathFieldRef.current.focus();
            showVirtualKeyboard();
        }
    };

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isToolbarOpen) {
                closeToolbar();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isToolbarOpen]);

    return (
        <>
            <style>{`

        .math-container {
          max-width: 900px;
          width: 100%;
          margin: 0;
          padding: 1rem 1rem;
          background: #ffffff;
          border-radius: 16px 16px 0px 0px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.35);
        }

        .latex-output {
          font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 0.85rem;
          background: #111827;
          color: #e5e7eb;
          padding: 0.6rem 0.75rem;
          border-radius: 8px;
          overflow-x: auto;
          white-space: pre-wrap;
          margin-top: 0.5rem;
        }

        .math-toolbar-body {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          margin-top: 1.5rem;
        }

        math-field {
          width: 100%;
          min-height: 8rem;
          white-space: normal !important;
          border-radius: 8px;
          border: 1px solid #d0d7de;
          padding: 0.5rem 0.75rem;
          box-sizing: border-box;
          font-size: 1.3rem;
          background: #fafbfc;
        }

        .math-toolbar-buttons {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.45rem;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .math-toolbar-buttons-left {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
        }

        .math-toolbar-header {
            display: flex; 
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 1rem;
            margin-bottom: 1rem;
        }

        math-code {
            display: flex; 
            padding: 0.125rem 0.375rem;
            border-radius: 4px;
            font-size: 0.875em;
        }
      `}</style>
            {mathEditor && (<div>
                <div className='math-container'>
                    <div
                        className="math-toolbar-backdrop"
                        onClick={handleBackdropClick}
                        aria-hidden="false"
                    >
                        <div className="math-toolbar" role="dialog" aria-modal="true" aria-label="Introdu formulă matematică">
                            <div className="math-toolbar-header flex">
                                <div className="math-toolbar-title">
                                    <span>Introdu formulă matematică</span>
                                </div>

                                <button
                                    type="button"
                                    className="math-toolbar-close"
                                    onClick={closeToolbar}
                                    title="Închide"
                                    aria-label="Închide"
                                >
                                    <XCircle />
                                </button>
                            </div>

                            <div className="math-toolbar-body">
                                <math-field
                                    ref={mathFieldRef}
                                    virtual-keyboard-mode="onfocus"
                                    virtual-keyboard-theme="material"
                                    virtual-keyboard-layout="qwerty"
                                    smart-fence="true"
                                    smart-mode="true"
                                    onInput={handleMathFieldInput}
                                    onPointerDown={handleMathFieldPointerDown}
                                >
                                    Ex.: x^2 + \sqrt{'{'}3{'}'} = 77
                                </math-field>

                                <div className="math-toolbar-buttons">
                                    <div className="math-toolbar-buttons-left">
                                        <Button className="px-2 py-2 h-10" variant={"icon"} onClick={insertFormula}>
                                            <Plus width={22} />
                                            Inserează formula
                                        </Button>
                                        <Button className="px-2 py-2 h-10" variant={"icon"} onClick={clearMath}>
                                            <Trash width={22} />
                                            Golește formula
                                        </Button>
                                    </div>

                                    <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                                        <code className="math-code">Formula va fi inserată într-un format matematic LaTeX.</code>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}
        </>
    );
}